import { type JSONContent } from "@tiptap/core";
import { nanoid } from "nanoid";
import { logWarning } from "@/lib/logging";
import { ERROR_IDS } from "@/lib/constants/errorIds";
import {
  ALLOWED_NOTE_LINK_SCHEMES,
  TOP_LEVEL_BLOCK_TYPE_SET,
  type TopLevelBlockType,
} from "./extensions";

export function cloneJson<T>(value: T): T {
  return structuredClone(value);
}

export function createBlockId() {
  return `blk_${nanoid(10)}`;
}

/**
 * Discriminated result so callers must explicitly handle the drop case —
 * prevents the "rejected URL becomes href=''" foot-gun that downstream
 * renderers would happily serialize.
 */
export type NoteLinkHrefValidation =
  | { readonly ok: true; readonly href: string }
  | { readonly ok: false; readonly reason: "empty" | "disallowed_scheme" | "invalid_url" };

export function validateNoteLinkHref(href: string): NoteLinkHrefValidation {
  const trimmed = href.trim();
  if (!trimmed) {
    return { ok: false, reason: "empty" };
  }

  // Same-document anchors (e.g. `#section-id`) bypass URL parsing so we don't
  // false-reject them. They never carry a scheme, so there's nothing to strip.
  if (trimmed.startsWith("#")) {
    return { ok: true, href: trimmed };
  }

  try {
    const parsed = new URL(trimmed, "https://notes.local");
    const protocol = parsed.protocol.replace(/:$/, "").toLowerCase();
    if (!ALLOWED_NOTE_LINK_SCHEMES.includes(protocol as (typeof ALLOWED_NOTE_LINK_SCHEMES)[number])) {
      logWarning("[tiptap-document] Stripped disallowed URL scheme", {
        errorId: ERROR_IDS.NOTES_SANITIZE_URL_FAILED,
        href: trimmed.slice(0, 120),
      });
      return { ok: false, reason: "disallowed_scheme" };
    }

    const normalized = parsed.origin === "https://notes.local" ? trimmed : parsed.toString();
    return { ok: true, href: normalized };
  } catch (urlError) {
    logWarning("[tiptap-document] Stripped invalid URL", {
      errorId: ERROR_IDS.NOTES_SANITIZE_URL_FAILED,
      href: trimmed.slice(0, 120),
      reason: urlError instanceof Error ? urlError.message : String(urlError),
    });
    return { ok: false, reason: "invalid_url" };
  }
}

export function sanitizeNode(node: JSONContent): JSONContent {
  const next = cloneJson(node);

  if (next.marks) {
    // Drop link marks whose href fails validation entirely — don't persist
    // `href=""` or keep the mark with a missing attribute. Non-link marks pass
    // through untouched.
    next.marks = next.marks.flatMap((mark) => {
      if (mark.type !== "link") return [mark];
      const rawHref = typeof mark.attrs?.href === "string" ? mark.attrs.href : "";
      const result = validateNoteLinkHref(rawHref);
      if (!result.ok) return [];
      return [{
        ...mark,
        attrs: {
          ...mark.attrs,
          href: result.href,
        },
      }];
    });
  }

  // Strip null-valued attrs so ProseMirror-propagated `{blockId: null}` on
  // nested blocks normalizes to the same shape as documents that omit the
  // key. `next` is a fresh `cloneJson` output, so in-place mutation is safe.
  if (next.attrs) {
    for (const key of Object.keys(next.attrs)) {
      if (next.attrs[key] == null) delete next.attrs[key];
    }
    if (Object.keys(next.attrs).length === 0) delete next.attrs;
  }

  if (next.content) {
    next.content = next.content.map(sanitizeNode);
  }

  return next;
}

function isTopLevelBlock(
  node: JSONContent
): node is JSONContent & { type: TopLevelBlockType } {
  return (
    typeof node.type === "string" &&
    TOP_LEVEL_BLOCK_TYPE_SET.has(node.type as TopLevelBlockType)
  );
}

/**
 * Normalize a top-level block, MINTING a random `blockId` when one is
 * missing or blank. Used by `parseNotesDocument` so every document that
 * crosses the parse boundary (AI output, markdown imports) ends up with a
 * stable id on each top-level block regardless of whether the producer
 * supplied one.
 */
export function normalizeTopLevelBlockWithMint(node: JSONContent): JSONContent {
  const next = sanitizeNode(node);

  if (!isTopLevelBlock(next)) {
    return next;
  }

  return {
    ...next,
    attrs: {
      ...next.attrs,
      blockId:
        typeof next.attrs?.blockId === "string" && next.attrs.blockId.trim().length > 0
          ? next.attrs.blockId
          : createBlockId(),
    },
  };
}

/**
 * Count how many top-level blocks in a (schema-parsed) content array lack
 * a non-empty `blockId`. Used by `parseNotesDocument` to decide whether to
 * mint and emit an observability signal.
 *
 * The `notesDocumentSchema` union only admits top-level block types, so
 * every entry is already a top-level block by the time this runs — no
 * `isTopLevelBlock` guard needed at the call sites.
 */
export function findTopLevelBlocksWithoutBlockId(content: readonly JSONContent[]): number {
  let count = 0;
  for (const node of content) {
    const raw = node.attrs?.blockId;
    if (typeof raw !== "string" || raw.trim().length === 0) count += 1;
  }
  return count;
}

export function collapseAdjacentTaskLists(content: JSONContent[]): JSONContent[] {
  const collapsed: JSONContent[] = [];

  for (const node of content) {
    const prev = collapsed[collapsed.length - 1];
    if (node.type === "taskList" && prev?.type === "taskList") {
      prev.content = [...(prev.content ?? []), ...(node.content ?? [])];
      continue;
    }
    collapsed.push(node);
  }

  return collapsed;
}
